import * as Sentry from '@sentry/react';
import {
  ChakraProvider, createSystem, defaultConfig, Dialog, Flex, Heading, Portal, VStack,
} from '@chakra-ui/react';
import React, {
  lazy, Suspense, useEffect,
} from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { PwaPromptProvider } from 'contexts/pwaPrompt';
import ComandaContextOutlet from 'components/ComandaContextOutlet';
import LoadingScreen from 'screens/Loading';
import Manager from 'Manager';
import NotFoundScreen from 'screens/NotFound';
import PublicRoutes from 'PublicRoutes';
import { ColorModeProvider } from 'components/ui/color-mode';
import { Toaster, toaster, useToast } from 'components/ui/toaster';
import { useOnline } from 'rooks';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { subscribeToSession } from 'redux/sessionSubscriptions';
import {
  selectSessionError,
  selectSessionProfile,
  selectSessionReady,
  selectSessionUser,
} from 'redux/slices/session.slice';

const LoginScreen = lazy(() => import('screens/Login'));
const ForgotPasswordScreen = lazy(() => import('screens/ForgotPassword'));
const SignUpScreen = lazy(() => import('screens/SignUp'));
const HomeScreen = lazy(() => import('screens/Home'));
const PaymentInstructionsScreen = lazy(() => import('screens/PaymentInstructions'));
const ReadCodeScreen = lazy(() => import('screens/ReadCode'));
const ProfileOptionsScreen = lazy(() => import('screens/ProfileOptions'));
const ProfileDatModal = lazy(() => import('screens/ProfileData'));
const CardsScreen = lazy(() => import('screens/Cards'));
const CardCreateScreen = lazy(() => import('screens/CardCreate'));
const AddressesScreen = lazy(() => import('screens/Addresses'));
const AddressCreateScreen = lazy(() => import('screens/AddressCreate'));
const HistoryScreen = lazy(() => import('screens/History'));
const VenueSearchScreen = lazy(() => import('screens/VenueSearch'));
const RewardsScreen = lazy(() => import('screens/Rewards'));
const BillLookupScreen = lazy(() => import('screens/BillLookup'));
const VenueMenuScreen = lazy(() => import('screens/VenueMenu'));
const CompaniesScreen = lazy(() => import('screens/Companies'));
const AddressCreateModal = lazy(() => import('screens/AddressCreate'));
const DocsScreen = lazy(() => import('screens/Docs'));

function AppRoutes() {
  const location = useLocation();
  const isSessionReady = useAppSelector(selectSessionReady);
  const sessionError = useAppSelector(selectSessionError);
  const user = useAppSelector(selectSessionUser);
  const profile = useAppSelector(selectSessionProfile);

  if (location.pathname === '/docs' || location.pathname.startsWith('/docs/')) {
    return (
      <Suspense fallback={<LoadingScreen description="Carregando documentação" />}>
        <DocsScreen />
      </Suspense>
    );
  }

  if (sessionError) {
    return (
      <NotFoundScreen
        title="Não foi possível verificar sua sessão"
        description={sessionError}
      />
    );
  }

  if (
    !isSessionReady
    // || session.user === undefined
    // || (session.user && session.userData === undefined)
    // || (!session.user && session.userData)
  ) return <LoadingScreen description="Verificando sessão" />;

  if (user && profile) {
    return (
      <Suspense fallback={<LoadingScreen description="Carregando módulo" />}>
        <Routes>
          <Route path="/" element={<HomeScreen />}>
            <Route path="/history" element={<HistoryScreen />} />
            <Route path="/profile" element={<ProfileOptionsScreen />} />
            <Route path="/venues" element={<VenueSearchScreen />} />
            <Route path="/" element={<PaymentInstructionsScreen />} />
          </Route>
          <Route path="/rewards" element={<RewardsScreen />} />
          <Route path="/profile/cards" element={<CardsScreen />} />
          <Route path="/profile/cards/new" element={<CardCreateScreen />} />
          <Route path="/profile/addresses" element={<AddressesScreen />} />
          <Route path="/profile/addresses/new" element={<AddressCreateScreen />} />
          <Route path="/delivery/:venueId" element={<ComandaContextOutlet type="delivery" />}>
            <Route index element={<VenueMenuScreen />} />
          </Route>
          <Route path="/p/:billId" element={<ComandaContextOutlet type="p" />}>
            <Route index element={<BillLookupScreen type="p" />} />
            <Route path="menu" element={<VenueMenuScreen />} />
          </Route>
          <Route path="/history/:billId" element={<ComandaContextOutlet type="h" />}>
            <Route index element={<BillLookupScreen type="h" />} />
            <Route
              path="receipt"
              element={<BillLookupScreen type="h" />}
            />
          </Route>
          <Route path="/signup" element={<LoadingScreen description="Signup" />} />
          {PublicRoutes}
          <Route key="CompaniesScreen" path="/empresas" element={<CompaniesScreen />} />
          <Route path="manager/*" element={<Manager />} />
          <Route path="*" element={<NotFoundScreen />} />
        </Routes>

        <AddressCreateModal />
        <ProfileDatModal />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/" element={<LoginScreen />} />
        <Route path="/read" element={<ReadCodeScreen />} />
        <Route path="/delivery/:venueId" element={<ComandaContextOutlet type="delivery" />}>
          <Route index element={<VenueMenuScreen />} />
        </Route>
        <Route path="/p/:billId" element={<ComandaContextOutlet type="p" />}>
          <Route index element={<BillLookupScreen type="p" />} />
          <Route path="menu" element={<VenueMenuScreen />} />
        </Route>
        <Route path="/history" element={<ComandaContextOutlet type="h" />}>
          <Route path="*" element={<BillLookupScreen type="h" />} />
        </Route>
        <Route path="/signup" element={<SignUpScreen />} />
        <Route path="/forgot" element={<ForgotPasswordScreen />} />
        {PublicRoutes}
        <Route
          path="/manager"
          element={(
            <NotFoundScreen
              title="Página protegida"
              description="Para acessar essa página, você precisa estar logado."
              backLinkText="Faça login para continuar"
            />
)}
        />
        <Route path="*" element={<NotFoundScreen />} />
      </Routes>
    </Suspense>
  );
}

const system = createSystem(defaultConfig, {
  theme: {
    tokens: {
      colors: {
        redComanda: {
          50: {
            value: '#f5e2e5',
          },
          100: {
            value: '#db667b',
          },
          200: {
            value: '#d54d65',
          },
          300: {
            value: '#cf334f',
          },
          400: {
            value: '#c91939',
          },
          500: {
            value: '#c30023',
          },
          600: {
            value: '#b00020',
          },
          700: {
            value: '#9c001c',
          },
          800: {
            value: '#890019',
          },
          900: {
            value: '#750015',
          },
        },
        blackSolid: {
          50: {
            value: '#cccccc',
          },
          100: {
            value: '#141414',
          },
          200: {
            value: '#121212',
          },
          300: {
            value: '#0f0f0f',
          },
          400: {
            value: '#0d0d0d',
          },
          500: {
            value: '#0a0a0a',
          },
          600: {
            value: '#2e2e2e',
          },
          700: {
            value: '#2b2b2b',
          },
          800: {
            value: '#030303',
          },
          900: {
            value: '#000000',
          },
        },
        whiteSolid: {
          50: {
            value: '#ffffff',
          },
          100: {
            value: '#ffffff',
          },
          200: {
            value: '#ffffff',
          },
          300: {
            value: '#ffffff',
          },
          400: {
            value: '#ffffff',
          },
          500: {
            value: '#ffffff',
          },
          600: {
            value: '#f2f2f2',
          },
          700: {
            value: '#e6e6e6',
          },
          800: {
            value: '#d9d9d9',
          },
          900: {
            value: '#cccccc',
          },
        },
      },
      fonts: {
        heading: { value: "'Fira Sans', sans-serif" },
        body: { value: "'Fira Sans', sans-serif" },
      },
    },
    semanticTokens: {
      colors: {
        redComanda: {
          contrast: { value: { _light: 'white', _dark: 'white' } },
          fg: { value: { _light: '{colors.redComanda.700}', _dark: '{colors.redComanda.200}' } },
          subtle: { value: { _light: '{colors.redComanda.100}', _dark: '{colors.redComanda.900}' } },
          muted: { value: { _light: '{colors.redComanda.200}', _dark: '{colors.redComanda.800}' } },
          emphasized: { value: { _light: '{colors.redComanda.300}', _dark: '{colors.redComanda.700}' } },
          solid: { value: '{colors.redComanda.500}' },
          focusRing: { value: '{colors.redComanda.500}' },
          border: { value: '{colors.redComanda.500}' },
        },
        blackSolid: {
          contrast: { value: 'white' },
          fg: { value: { _light: '{colors.blackSolid.900}', _dark: '{colors.blackSolid.50}' } },
          subtle: { value: { _light: '{colors.blackSolid.50}', _dark: '{colors.blackSolid.900}' } },
          muted: { value: { _light: '{colors.blackSolid.100}', _dark: '{colors.blackSolid.800}' } },
          emphasized: { value: { _light: '{colors.blackSolid.600}', _dark: '{colors.blackSolid.700}' } },
          solid: { value: '{colors.blackSolid.500}' },
          focusRing: { value: '{colors.blackSolid.500}' },
          border: { value: '{colors.blackSolid.500}' },
        },
        whiteSolid: {
          contrast: { value: 'black' },
          fg: { value: '{colors.whiteSolid.900}' },
          subtle: { value: '{colors.whiteSolid.50}' },
          muted: { value: '{colors.whiteSolid.600}' },
          emphasized: { value: '{colors.whiteSolid.700}' },
          solid: { value: '{colors.whiteSolid.500}' },
          focusRing: { value: '{colors.whiteSolid.900}' },
          border: { value: '{colors.whiteSolid.900}' },
        },
      },
    },
  },
});

function App() {
  const dispatch = useAppDispatch();
  const isOnline = useOnline();
  const toast = useToast();

  useEffect(() => dispatch(subscribeToSession()), [dispatch]);

  useEffect(() => {
    const downloadingToastId = 'service-worker-update-download';

    const handleServiceWorkerDownloading = () => {
      toast({
        id: downloadingToastId,
        status: 'loading',
        title: 'Atualização encontrada',
        description: 'Baixando os arquivos da nova versão…',
        isClosable: false,
        duration: Infinity,
      });
    };

    const handleServiceWorkerUpdate = (event: Event) => {
      const registration = (event as CustomEvent<ServiceWorkerRegistration>).detail;
      const waitingServiceWorker = registration?.waiting;

      if (!waitingServiceWorker) return;

      toaster.dismiss(downloadingToastId);

      toast({
        status: 'info',
        title: 'Nova versão disponível',
        description: 'Atualize a página para usar a versão mais recente.',
        isClosable: true,
        action: {
          label: 'Atualizar',
          onClick: () => {
            waitingServiceWorker.addEventListener('statechange', (stateChangeEvent) => {
              const target = stateChangeEvent.target as ServiceWorker;

              if (target.state === 'activated') {
                window.location.reload();
              }
            });
            waitingServiceWorker.postMessage({ type: 'SKIP_WAITING' });
          },
        },
      });
    };

    window.addEventListener('serviceworkerdownloading', handleServiceWorkerDownloading);
    window.addEventListener('serviceworkerupdate', handleServiceWorkerUpdate);
    return () => {
      window.removeEventListener('serviceworkerdownloading', handleServiceWorkerDownloading);
      window.removeEventListener('serviceworkerupdate', handleServiceWorkerUpdate);
      toaster.dismiss(downloadingToastId);
    };
  }, [toast]);

  return (
    <PwaPromptProvider>
      <ChakraProvider value={system}>
        <ColorModeProvider defaultTheme="light" enableSystem={false}>
          <Sentry.ErrorBoundary fallback={(
            <NotFoundScreen
              title="Ops... Aconteceu algum problema"
              description="Para continuar, você precisa atualizar a página."
              onBackLinkClick={() => {
                document.location.reload();
              }}
            />
)}
          >
            <Dialog.Root
              open={!isOnline}
              placement="center"
              onOpenChange={(e) => {
                if (!e.open) {
                //
                }
              }}
            >
              <Portal>
                <Dialog.Backdrop backdropFilter="blur(4px)" />
                <Dialog.Positioner>
                  <Dialog.Content>
                    <Dialog.Header textAlign="center">
                      <VStack width="100%">
                        <img style={{ height: 90, margin: 'auto' }} alt="offline" src="data:image/gif;base64,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" />
                        <Heading size="md" mt={10}>Conexão perdida</Heading>
                      </VStack>
                    </Dialog.Header>
                    <Dialog.Body textAlign="center" pb={18}>
                      Sua conexão com a internet foi perdida. Conecte-se novamente para continuar.
                    </Dialog.Body>
                  </Dialog.Content>
                </Dialog.Positioner>
              </Portal>
            </Dialog.Root>
            <Flex
              overflowY="auto"
              overflowX="hidden"
              flexGrow={1}
              height="100%"
              direction="column"
              marginX="auto"
              width={['100%', '100%', '100%', '100%']}
              boxShadow="0px 0px 65px 13px rgba(0,0,0,1)"
            >
              <AppRoutes />
            </Flex>
            <Toaster />
          </Sentry.ErrorBoundary>
        </ColorModeProvider>
      </ChakraProvider>
    </PwaPromptProvider>
  );
}

export default Sentry.withProfiler(App);
